import { type RefObject, useRef } from 'react';
import {
  MarketAccessory,
  MarketButton,
  MarketButtonDropdown,
  MarketIcon,
  MarketInputText,
  MarketList,
  MarketRow,
} from '@market/react';
import { useTranslation } from 'react-i18next';
import {
  LocationRow,
  type LocationRowState,
} from './LocationRow/LocationRow';
import styles from './LocationSelector.module.css';
import { EmptyState } from './EmptyState/EmptyState';

const MIN_LOCATIONS_FOR_SEARCH = 5;

export interface Location {
  rowState?: LocationRowState;
  locationId: string;
  locationLabel: string;
  selectLocation: () => void;
}

export interface LocationSelectorProps {
  readonly selectedLocationLabel: string;
  readonly selectedLocationId: string;
  readonly searchInputValue: string;
  readonly setSearchInputValue: (value: string) => void;
  readonly locations: Location[];
  readonly onOpen?: () => void;
  readonly dropdownRef: RefObject<HTMLMarketButtonDropdownElement>;
  readonly useCompactStyling?: boolean;
}

export const LocationSelector = ({
  selectedLocationLabel,
  locations,
  selectedLocationId,
  onOpen,
  setSearchInputValue,
  searchInputValue,
  dropdownRef,
  useCompactStyling,
}: LocationSelectorProps) => {
  const { t } = useTranslation();
  const initialLocationsLength = useRef(locations.length);

  return (
    <MarketButtonDropdown
      ref={dropdownRef}
      persistListSelections
      popoverPlacement="bottom"
      interaction="persistent"
      className={useCompactStyling ? `${styles.buttonDropdownCompact} ${styles.buttonDropdown}` : styles.buttonDropdown}
      onMarketButtonDropdownOpened={onOpen}
    >
      <MarketButton
        className={useCompactStyling ? `${styles.buttonInnerCompact} ${styles.buttonInner}` : styles.buttonInner}
        slot="trigger"
        rank="secondary"
        size="small"
        data-testid="location-selector-button"
      >
        {selectedLocationLabel}
      </MarketButton>

      <div slot="content" className={styles.menu}>
        {initialLocationsLength.current >= MIN_LOCATIONS_FOR_SEARCH
          ? (
            <MarketRow className={styles.searchRow}>
              <MarketInputText
                value={searchInputValue}
                data-testid="location-selector-input"
                onMarketInputValueChange={({ detail: { value } }) => setSearchInputValue(value)}
              >
                <label>{t('bankingShared.locationSelector.search')}</label>
                <MarketAccessory
                  slot="leading-accessory"
                  size="icon"
                  className={styles.searchIcon}
                >
                  <MarketIcon name="search" />
                </MarketAccessory>
              </MarketInputText>
            </MarketRow>
          )
          : null}

        {locations.length > 0
          ? (
            <MarketList value={selectedLocationId}>
              {locations.map((location) => (
                <LocationRow
                  key={location.locationId}
                  locationId={location.locationId}
                  locationLabel={location.locationLabel}
                  selectLocation={location.selectLocation}
                  state={location.rowState}
                />
              ))}
            </MarketList>
          )
          : (
            <EmptyState />
          )}
      </div>
    </MarketButtonDropdown>
  );
};
